/**
 * Application bootstrap. Wires URL params → API → state → Rive + Pixi layers,
 * then hands off to the orchestrator. Mirrors the spirit of
 *   apps/amg_dragontiger_v2/lib/main.dart
 * but as a single entry point since this build only has one route.
 *
 * Note: there are no DOM bet zones / chip picker / confirm-clear buttons —
 * those all live inside `dragontigerbettingui.riv` and emit Rive events
 * (placeBetClick / evtClickChips / evtClearClick / evtButtonPlayClick).
 */
import { Api } from './net/api';
import { MultiplayerHub } from './net/multiplayerHub';
import { GameState } from './game/gameState';
import { GameOrchestrator } from './game/gameOrchestrator';
import { loadUrlParams } from './game/urlParams';
import { RiveLayer } from './rive/riveLayer';
import { PixiLayer } from './pixi/pixiLayer';
import { ChipLayer } from './pixi/chipLayer';
import { Hud } from './ui/hud';
import { TableControls } from './ui/controls';
import { GameTimerWidget } from './ui/timerWidget';
import { GameLogWidget } from './ui/gameLogWidget';
import { PerfHud } from './perf/perfHud';
import { ResponsiveScaler } from './util/responsiveScaler';
import { SoundManager } from './audio/soundManager';
import { bindGameSounds, CHIP_SELECT_SFX, SFX_CLIPS } from './audio/soundWiring';
import { SoundToggle } from './ui/soundToggle';
import { MULTIPLAYER_HUB_URL } from './config';
import { debugLog, error as logError } from './util/logger';

declare global {
  interface Window {
    // Defined by the inline loading-splash controller in index.html.
    hideLoadingScreen?: () => void;
  }
}

async function bootstrap(): Promise<void> {
  if (!('WebGL2RenderingContext' in window)) {
    showFatal('Your browser does not support WebGL2. Please use a modern browser.');
    return;
  }

  const scaler = new ResponsiveScaler('stage');
  scaler.attach();

  const params = loadUrlParams();
  debugLog('[main] URL params', params);

  const riveMain = document.getElementById('rive-canvas-main') as HTMLCanvasElement;
  const rivePopup = document.getElementById('rive-canvas-popup') as HTMLCanvasElement;
  const riveBetting = document.getElementById('rive-canvas-betting') as HTMLCanvasElement;
  const riveWinlose = document.getElementById('rive-canvas-winlose') as HTMLCanvasElement;
  const pixiCanvas = document.getElementById('pixi-canvas') as HTMLCanvasElement;
  if (!riveMain || !rivePopup || !riveBetting || !riveWinlose || !pixiCanvas) {
    showFatal('Stage canvases missing from DOM.');
    return;
  }

  const rive = new RiveLayer({
    canvases: {
      main: riveMain,
      popup: rivePopup,
      betting: riveBetting,
      winlose: riveWinlose,
    },
  });
  const pixi = new PixiLayer(pixiCanvas);

  const perfHud = new PerfHud();
  perfHud.start();

  // SFX. Preload now; `bindGameSounds` subscribes once Rive is ready. Audio is
  // unlocked on the first user gesture so programmatic plays aren't blocked by
  // the browser autoplay policy.
  const sound = new SoundManager();
  sound.preload(SFX_CLIPS);
  window.addEventListener('pointerdown', sound.unlock, { once: true });
  window.addEventListener('keydown', sound.unlock, { once: true });
  // Top-right sound on/off toggle. Drives SoundManager only — the Rive
  // `IsSoundOn` input stays true so sound cues keep arriving. Persists choice.
  void new SoundToggle({ onChange: (on) => sound.setEnabled(on) });

  const state = new GameState();
  // ClickBetSelect plays for two player actions, both read off the GameState
  // chokepoint (covers the DOM picker and any Rive-driven change) and gated by
  // the sound on/off toggle via SoundManager:
  //   - selecting a chip denomination (selectedChipIndex changes)
  //   - placing a bet on the table  (bet total increases — ignores clear/reset
  //     and rejected bets, which don't raise the total)
  state.on((next, prev) => {
    const chipSelected = next.selectedChipIndex !== prev.selectedChipIndex;
    const betPlaced = next.bets.total > prev.bets.total;
    if (chipSelected || betPlaced) {
      sound.play(CHIP_SELECT_SFX);
    }
  });
  const api = new Api();
  const hub = new MultiplayerHub(MULTIPLAYER_HUB_URL);
  const hud = new Hud();
  const timer = new GameTimerWidget();
  // Bet-history log button — top-right. Self-mounts into #dom-hud and
  // subscribes to the GameLogManager singleton.
  void new GameLogWidget();

  // Build the orchestrator BEFORE controls so the controls can call into it
  // — but the orchestrator needs the controls instance for state-sync. We
  // bridge with a deferred ref.
  let orchestratorRef: GameOrchestrator | null = null;
  const controls = new TableControls({
    onSelectChip: (i) => state.selectChip(i),
    onClear: () => orchestratorRef?.clearAll(),
    onConfirm: () => void orchestratorRef?.confirm(),
  });

  try {
    await Promise.all([pixi.init(), rive.init()]);
  } catch (e) {
    logError('[main] init failed', e);
    showFatal(`Failed to load assets: ${(e as Error)?.message ?? String(e)}`);
    return;
  }
  state.setRiveReady(true);

  bindGameSounds(rive, sound);

  const chips = new ChipLayer(pixi.root);

  const orchestrator = new GameOrchestrator({
    state,
    api,
    rive,
    chips,
    hud,
    controls,
    hub,
    timer,
    params,
    onInteractive: () => {
      perfHud.markInteractive();
      // Game is signed in, configured and rendering — drop the loading splash.
      window.hideLoadingScreen?.();
    },
  });
  orchestratorRef = orchestrator;

  await orchestrator.start();
}

function showFatal(message: string): void {
  // Drop the loading splash first — it sits above the fatal overlay, so the
  // error would otherwise be hidden behind it.
  window.hideLoadingScreen?.();
  const overlay = document.getElementById('fatal-overlay');
  const msg = document.getElementById('fatal-message');
  if (msg) msg.textContent = message;
  if (overlay) overlay.style.display = 'flex';
}

window.addEventListener('error', (e) => logError('[window.error]', e.error ?? e.message));
window.addEventListener('unhandledrejection', (e) =>
  logError('[unhandledrejection]', e.reason),
);

bootstrap().catch((e) => {
  logError('[main] bootstrap crashed', e);
  showFatal(`Bootstrap failed: ${(e as Error)?.message ?? String(e)}`);
});
